import clsx from 'clsx';

import { Field, fieldStyles, getFieldIconColor, useFieldId } from '../Field';
import { Typography } from '../typography';

import type { FieldSize, FieldVariant } from '../Field';
import type { DetailedHTMLProps, InputHTMLAttributes, ReactNode } from 'react';

import s from './InputField.module.css';

type InputFieldSize = FieldSize;
type InputFieldVariant = FieldVariant;

export type InputFieldProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'prefix' | 'size'
> & {
  /** Additional element after prefix (e.g., dropdown, selector) */
  accessor?: ReactNode;
  /** Container className */
  containerClassName?: string;
  /** Make input take full width of its container */
  fullWidth?: boolean;
  /** Helper text displayed below the input */
  hint?: ReactNode;
  /** Input className */
  inputClassName?: string;
  /** Label text displayed at the end (shows as placeholder if no label) */
  label?: string;
  /** Icon or element displayed at the start of the input */
  prefix?: ReactNode;
  /** Size of the input field
   * @default 'default'
   */
  size?: InputFieldSize;
  /** Element displayed after accessor with gap */
  suffix?: ReactNode;
  /** Visual state of the input
   * @default 'default'
   */
  variant?: InputFieldVariant;
};

export const InputField = ({
  accessor,
  className,
  containerClassName,
  disabled,
  fullWidth,
  hint,
  id,
  label,
  placeholder,
  prefix,
  required,
  size = 'default',
  suffix,
  value,
  variant = 'default',
  ...props
}: InputFieldProps) => {
  const inputId = useFieldId(id);

  const hasLabel = Boolean(label);
  const hasPrefix = Boolean(prefix);
  const hasAccessor = Boolean(accessor);
  const hasSuffix = Boolean(suffix);

  const placeholderText = placeholder ?? label ?? undefined;

  const iconColor = getFieldIconColor(variant, disabled);
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <Field
      className={containerClassName}
      disabled={disabled}
      fullWidth={fullWidth}
      hint={hint}
      hintId={hintId}
      variant={variant}
    >
      <Field.Wrapper
        hasPrefix={hasPrefix}
        disabled={disabled}
        fullWidth={fullWidth}
        hasSuffix={hasSuffix}
        size={size}
        variant={variant}
      >
        <label className={fieldStyles.labelWrapper} htmlFor={inputId}>
          {hasPrefix && <Field.Prefix color={iconColor}>{prefix}</Field.Prefix>}

          {hasAccessor && (
            <Typography className={s.accessor} color="white" component="span" variant="inset-paragraph">
              {accessor}
            </Typography>
          )}

          <input
            aria-describedby={hintId}
            aria-invalid={variant === 'danger'}
            aria-required={required}
            className={clsx(fieldStyles.control, fieldStyles[`control--variant-${variant}`], className)}
            disabled={disabled}
            id={inputId}
            placeholder={placeholderText}
            required={required}
            value={value}
            {...props}
          />

          {hasLabel && Boolean(value) && (
            <Typography className={s.label} color={iconColor} component="span" variant="inset-paragraph">
              {label}
            </Typography>
          )}

          {hasSuffix && <Field.Suffix color={iconColor}>{suffix}</Field.Suffix>}
        </label>
      </Field.Wrapper>
    </Field>
  );
};
